/**
 * Query params that change which page a visitor actually sees, and therefore deserve a canonical
 * URL of their own.
 *
 * `type` qualifies: it switches the Mini PDP between the dog and cat products, picks the SKU type on
 * supplements, and routes cat referrals to a different product entirely. Dropping it would collapse
 * pages that rank independently.
 *
 * `selected` qualifies on the pick-your-fi plp: it selects which tracker the page opens on, so
 * `?selected=ultra` and the bare url are different pages to a visitor arriving from an ad.
 *
 * `products` qualifies there too: it names the lineup on the page. A bare `/shop` sells Mini and
 * Series 3+, while `?products=mini,s3,ultra` is the listing with every tracker on it, which is the
 * one the sitemap advertises. Collapsing it onto the bare url would have Google index the page
 * without Ultra.
 *
 * Everything else the storefront reads from the query string is presentational or attribution —
 * `color`/`size` merely preselect a swatch on a PDP whose content is otherwise identical, and
 * `ref`, `returnTo`, `utm_*`, `fbclid` and friends change nothing at all.
 */
const CONTENT_BEARING_PARAMS = new Set(['products', 'selected', 'type']);

/**
 * Reduces a location's query string to the params worth indexing.
 *
 * SeoHelmet built its canonical (and hreflang) URLs from the raw query string, so every parameter
 * combination self-canonicalised. Search Console duly indexed `?color=pink&size=medium`,
 * `?ref=blog.tryfi.com` and `?returnTo=%2Fmanage` as separate pages, splitting signals across URLs
 * that render the same thing.
 *
 * Params are emitted in a stable order so a given page always resolves to exactly one canonical URL,
 * however the visitor's link happened to be ordered.
 */
export default function canonicalSearch(search: string): string {
  const kept = [...new URLSearchParams(search).entries()]
    .filter(([key]) => CONTENT_BEARING_PARAMS.has(key))
    .sort(([a], [b]) => a.localeCompare(b));

  // URLSearchParams percent-encodes the commas in a `products` lineup; keep them literal so the
  // canonical reads exactly like the sitemap entry and the links that carry it.
  return kept.length > 0 ? `?${new URLSearchParams(kept).toString().replace(/%2C/g, ',')}` : '';
}
